import {
  LargeInput,
  LargeInputIconButton,
  LargeInputWithIconButton,
  ValidationContainer,
  ValidationMessage,
  Dialog,
  DialogButton,
  DialogCloseX,
  DialogContent,
  DialogDescription,
  DialogHeading,
  CalendarOutlineIcon,
  UnstyledInput,
} from "@tandemdiabetes/sugarmate-react-components"
import dayjs from "dayjs"
import {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react"
import type {
  ChangeEventHandler,
  MouseEventHandler,
} from "react"

import styles from "../AccountForms.module.scss"
import customStyles from "./PersonalInformationForm.module.scss"
import { RetriggerValidation } from "./RetriggerValidation"

import {
  usePolyglot,
  useCountrySelectionContext,
} from "src/contexts"
import {
  useCountries,
  useToday,
} from "src/hooks"
import {
  getLocaleDateFormat,
  getLocaleDateStringFromDate,
  getDateFromLocaleDateString,
} from "src/utils"

const polyglotPrefix = "components.account_forms.personal_information.date_of_birth."
const dateOfBirthId = "date-of-birth-id"

const dateOfBirthErrorId = "date-of-birth-error-id"
const dateInputFormat = "YYYY-MM-DD"

interface Props {
  value: string;
  onChange: (v: string) => void;
}

export function DateOfBirthInput(props: Props) {
  const {
    value,
    onChange: propsOnChange,
  } = props

  const polyglot = usePolyglot()
  const countries = useCountries().data
  const today = useToday()

  const [
    isDialogOpen,
    setIsDialogOpen,
  ] = useState<boolean>(false)

  const { countryCode } = useCountrySelectionContext()

  const minimumAge = useMemo(
    () => {
      const currentCountry = (countries ?? []).find(
        (country) => country.country_code === countryCode,
      )

      if (currentCountry) {
        return currentCountry.minimum_age
      }

      return 18
    },
    [
      countries,
      countryCode,
    ],
  )

  const latestValidDateOfBirth = useMemo(
    () => dayjs(today)
      .subtract(
        minimumAge,
        "years",
      )
      .startOf("day"),
    [
      today,
      minimumAge,
    ],
  )

  const earliestValidDateOfBirth = useMemo(
    () => dayjs(today)
      .subtract(
        150,
        "years",
      )
      .startOf("day"),
    [today],
  )

  // hasBlurred will be true if the user has blurred the input at least once
  const [
    hasBlurred,
    setHasBlurred,
  ] = useState<boolean>(false)

  const inputRef = useRef<HTMLInputElement>(null)

  const {
    dateFormat: inputPattern,
    separators,
  } = useMemo(
    () => getLocaleDateFormat({
      language: polyglot.locale(),
      dayAbbreviation: "\\d",
      monthAbbreviation: "\\d",
      yearAbbreviation: "\\d",
      escapeSeparatorRegexp: true,
    }),
    [polyglot],
  )

  const [
    customError,
    setCustomError,
  ] = useState("")

  const updateCustomError = useCallback(
    (error: string) => {
      inputRef.current?.setCustomValidity(error)
      setCustomError(error)
    },
    [
      setCustomError,
      inputRef,
    ],
  )

  const validateAge = useCallback(
    (userInput: string) => {
      const newValue = getDateFromLocaleDateString(
        polyglot.locale(),
        userInput,
      )
      const isValidDate = !isNaN(newValue.valueOf())

      const inputRegex = new RegExp(inputPattern)
      const inputMatchesPattern = inputRegex.test(userInput)

      if (isValidDate) {
        const isTooYoung = dayjs(newValue)
          .startOf("day")
          .isAfter(latestValidDateOfBirth)

        const isTooOld = dayjs(newValue)
          .startOf("day")
          .isBefore(earliestValidDateOfBirth)

        const isInFuture = dayjs(newValue).isAfter(today)

        if (isTooOld || isInFuture) {
          updateCustomError(polyglot.t(`${polyglotPrefix}invalid_error`))
        } else if (isTooYoung) {
          updateCustomError(polyglot.t(
            `${polyglotPrefix}too_young_error`,
            minimumAge,
          ))
        } else {
          updateCustomError("")
        }
      } else if (inputMatchesPattern) {
        updateCustomError(polyglot.t(`${polyglotPrefix}invalid_error`))
      } else {
        updateCustomError("")
      }
    },
    [
      minimumAge,
      latestValidDateOfBirth,
      earliestValidDateOfBirth,
      today,
      inputPattern,
      polyglot,
      updateCustomError,
    ],
  )

  /*
   * show the warning dialog if the user has picked date that would make them
   * less than 18 years old
   */
  useEffect(
    () => {
      const date = getDateFromLocaleDateString(
        polyglot.locale(),
        value,
      )
      const isValidDate = !isNaN(date.valueOf())

      if (!value || !hasBlurred || !isValidDate) {
        return
      }

      const isTooYoung = dayjs(date)
        .startOf("day")
        .isAfter(latestValidDateOfBirth)

      const isInFuture = dayjs(date).isAfter(today)

      if (isTooYoung && !isInFuture) {
        setIsDialogOpen(true)
      }
    },
    [
      today,
      latestValidDateOfBirth,
      value,
      hasBlurred,
      polyglot,
    ],
  )

  const onBlur = useCallback(
    () => {
      // update the hasBlurred state
      setHasBlurred(true)
    },
    [setHasBlurred],
  )

  const {
    dateFormat,
    dateFormatLength,
  } = useMemo(
    () => getLocaleDateFormat({
      language: polyglot.locale(),
      dayAbbreviation: polyglot.t(`${polyglotPrefix}day_abbreviation`),
      monthAbbreviation: polyglot.t(`${polyglotPrefix}month_abbreviation`),
      yearAbbreviation: polyglot.t(`${polyglotPrefix}year_abbreviation`),
    }),
    [polyglot],
  )

  const dateInputRef = useRef<HTMLInputElement>(null)

  const openDatePicker: MouseEventHandler<HTMLButtonElement> = useCallback(
    () => {
      const dateInput = dateInputRef.current

      if (!dateInput) {
        return
      }

      // on iOS the picker will only show if the input is focused first
      dateInput.focus()

      if (typeof dateInput.showPicker === "function") {
        try {
          // Keep the modern behavior when supported.
          dateInput.showPicker()
          return
        } catch {
          // Some older WebKit builds expose inconsistent behavior.
        }
      }

      // Older iOS Safari does not support showPicker(), but click opens date UI.
      dateInput.click()
    },
    [dateInputRef],
  )

  // this controls the value of the date input
  const [
    dateInputValue,
    setDateInputValue,
  ] = useState(dayjs().format(dateInputFormat))

  // when the dateOfBirth value is a valid date, update the date input value
  useEffect(
    () => {
      const date = getDateFromLocaleDateString(
        polyglot.locale(),
        value,
      )
      const isValidDate = !isNaN(date.valueOf())

      if (isValidDate && dateInputRef.current) {
        setDateInputValue(dayjs(date).format(dateInputFormat))
      }
    },
    [
      value,
      setDateInputValue,
      polyglot,
    ],
  )

  const onDateInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (ev) => {
      const newValue = ev.target.value

      /*
       * constructs a dayjs instance then converts to date because it handles
       * time zones better than new Date(value)
       */
      const date = dayjs(newValue).toDate()
      const dateString = getLocaleDateStringFromDate(
        polyglot.locale(),
        date,
      )

      propsOnChange(dateString)

      validateAge(dateString)

      /*
       * focusing and immediately blurring the input element forces it to
       * re-evaluate its validity, see the ValidationContext
       */
      inputRef.current?.focus()
      inputRef.current?.blur()
    },
    [
      propsOnChange,
      validateAge,
      inputRef,
      polyglot,
    ],
  )

  // this regexp only matches the characters that we accept in the input
  const regExp = useMemo(
    () => {

      const separatorValues = separators.reduce(
        (accumulator, separator) => `${accumulator}${separator.value}`,
        "",
      )

      return new RegExp(
        `[^\\d${separatorValues}]`,
        "g",
      )
    },
    [separators],
  )

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (ev) => {
      // remove everything that isn't a number or a separator
      const newValue = ev.target.value.replaceAll(
        regExp,
        "",
      )

      propsOnChange(newValue)
      validateAge(newValue)
    },
    [
      regExp,
      validateAge,
      propsOnChange,
    ],
  )

  return (
    <>
      <ValidationContainer>
        <LargeInputWithIconButton>
          <UnstyledInput
            type="date"
            autoComplete="bday"
            tabIndex={-1}
            ref={dateInputRef}
            className={customStyles.dateInput}
            onChange={onDateInputChange}
            min={dayjs(earliestValidDateOfBirth).format(dateInputFormat)}
            max={dayjs(latestValidDateOfBirth).format(dateInputFormat)}
            value={dateInputValue}
          />
          <RetriggerValidation
            updateCustomErrorCallback={validateAge}
            dependency={latestValidDateOfBirth.toISOString()}
            inputRef={inputRef}
          />
          <LargeInput
            ref={inputRef}
            id={dateOfBirthId}
            placeholder={polyglot.t(
              `${polyglotPrefix}placeholder`,
              { dateFormat },
            )}
            className={styles.input}
            type="text"
            value={value}
            onChange={onChange}
            autoComplete="bday"
            required
            minLength={dateFormatLength}
            maxLength={dateFormatLength}
            pattern={inputPattern}
            onBlur={onBlur}
          />
          <LargeInputIconButton
            placementPreset="end"
            onClick={openDatePicker}
          >
            <CalendarOutlineIcon />
          </LargeInputIconButton>
        </LargeInputWithIconButton>
        <ValidationMessage
          id={dateOfBirthErrorId}
          invalidMessage={{
            patternMismatch: polyglot.t(
              `${polyglotPrefix}pattern_mismatch`,
              { dateFormat },
            ),
            customError,
          }}
        />
      </ValidationContainer>
      <Dialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      >
        <DialogContent
          id="age-limit-dialog"
        >
          <DialogHeading>
            {polyglot.t(`${polyglotPrefix}dialog.heading`)}
            <DialogCloseX />
          </DialogHeading>
          <DialogDescription>
            {polyglot.t(
              `${polyglotPrefix}dialog.description`,
              minimumAge,
            )}
          </DialogDescription>
          <DialogButton
            id="age-limit-dialog-btn"
          >
            {polyglot.t(`${polyglotPrefix}dialog.confirm_cta`)}
          </DialogButton>
        </DialogContent>
      </Dialog>
    </>
  )
}
